import { useState, useEffect, useCallback, useMemo } from 'react';
import { glossaryService } from 'services/api';
import TermFormModal from './TermFormModal';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import useCierreAlerta from 'utils/useCierreAlerta';
import styles from './Glosario.module.css';

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export default function Glosario() {
  const [terms, setTerms] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState(null); // null | {} | term
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState('');
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const cerrarAlertaEn = useCierreAlerta(setAlert);

  const showAlert = (type, message) => { setAlert({ type, message }); cerrarAlertaEn(3500); };

  const load = useCallback(async () => {
    try {
      const r = await glossaryService.getAll();
      setTerms(r.data.terms || []);
      setCanManage(!!r.data.can_manage);
    } catch (e) { setError(e.response?.data?.message || 'No se pudieron cargar los términos.'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    setDelError('');
    try {
      await glossaryService.delete(confirmDel.id);
      setConfirmDel(null);
      await load();
      showAlert('success', 'Término eliminado');
    } catch (e) {
      setDelError(e.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.');
    } finally { setDeleting(false); }
  };

  const filtered = useMemo(() => {
    if (!terms) return [];
    const nq = norm(q.trim());
    if (!nq) return terms;
    return terms.filter(t => norm(t.term).includes(nq) || norm(t.definition).includes(nq));
  }, [terms, q]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-book ${styles.iconoCabecera}`} />Glosario</h1>
          <p>Términos de las asambleas explicados en lenguaje sencillo, para que los
            propietarios los consulten desde su panel.{' '}
            {canManage
              ? 'Son comunes a toda la plataforma: los mismos para todos los conjuntos.'
              : 'Solo el super administrador puede editarlos.'}</p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => setEditor({})}>
            <i className="fas fa-plus" /> Nuevo término
          </button>
        )}
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}
      {error && <div className="alert alert-danger" onClick={() => setError('')}>{error}</div>}

      {terms !== null && terms.length > 0 && (
        <div className={styles.buscadorCaja}>
          <i className={`fas fa-search ${styles.iconoBuscar}`} />
          <input className={`form-control ${styles.buscador}`} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Buscar término…" />
        </div>
      )}

      {terms === null ? (
        <div className="loading"><div className="spinner" /><p>Cargando...</p></div>
      ) : terms.length === 0 ? (
        <div className="card"><div className="empty-state">
          <i className="fas fa-book" />
          <p>No hay términos todavía.</p>
        </div></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state">
          <i className="fas fa-inbox" />
          <p>Ningún término coincide con «{q}».</p>
        </div></div>
      ) : (
        <div className={styles.lista}>
          {filtered.map(t => (
            <div key={t.id} className={`card ${styles.tarjeta}`}>
              <div className={styles.fila}>
                {/* overflowWrap: un término o una definición de miles de caracteres sin espacios se partía fuera de la tarjeta y quedaba recortado */}
                <div className={styles.texto}>
                  <div className={styles.termino}>
                    {t.term}
                    {canManage && (
                      <span className={styles.orden}>
                        orden {t.sort_order}
                      </span>
                    )}
                  </div>
                  <div className={styles.definicion}>{t.definition}</div>
                </div>
                {canManage && (
                  <div className={styles.botones}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditor(t)}>
                      <i className="fas fa-pencil" />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => setConfirmDel(t)}>
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editor && (
        <TermFormModal
          initial={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onDone={load}
        />
      )}

      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar término" onClose={() => { setConfirmDel(null); setDelError(''); }} onConfirm={del} deleting={deleting} error={delError}>
          <p>¿Eliminar el término <strong>{confirmDel.term}</strong>?</p>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}
